import React, { Fragment, useState } from 'react';
import { Spin } from 'antd';
import FeaturePopover from './FeaturePopover';
import { useLayerProperitesContext } from '../../../context/LayerPropertiesContext';
import { useLegendUI } from '../../../context/MapLegendContext';
import MapSearch from './MapSearch';
import MeasurePanel from './MeasurePanel';
import { useMeasureTool } from '../../../hooks/useMeasureTool';
import type { MeasureMode } from '../../../hooks/useMeasureTool';
import { useCesiumViewer } from '../../../hooks/useCesiumViewer';
import { useCesiumCamera } from '../../../hooks/useCesiumCamera';
import { useCesiumLayers } from '../../../hooks/useCesiumLayers';
import { useCesiumEffects } from '../../../hooks/useCesiumEffects';
import { useCesiumPopup } from '../../../hooks/useCesiumPopup';
import './MapPortalMap.scss';
import * as Cesium from 'cesium';
// import { useLegendUI } from "../../../context/MapLegendContext";

export interface PopoverContent {
  id?: string;
  photos?: string;
  [key: string]: string | undefined;
}

interface MapPortalMapProps {
  containerId: string;
  onFeatureSelect?: (id: string, layerName: string) => void;
  layers: never[];
  boundingLayers: any;
  drawingMode: string;
  currentTool: string;
  setCurrentTool: (value: string) => void;
  viewerRef: any;
  defaultMapPositions: any;
  mapId?: string | number;
  isLayerLoading?: boolean;
  popupsToggle?: boolean;
}

const MapPortalMap: React.FC<MapPortalMapProps> = ({
  containerId,
  layers,
  boundingLayers,
  currentTool,
  setCurrentTool,
  viewerRef,
  defaultMapPositions,
  mapId,
  isLayerLoading = false,
}) => {
  const [popoverVisible, setPopoverVisible] = useState<boolean>(false);
  const [popoverContent, setPopoverContent] = useState<PopoverContent>({});
  // Popup chrome for the *clicked* layer only (not the Layer Properties editor context).
  const [activePopupConfig, setActivePopupConfig] = useState<{
    popupTitle?: string;
    popupTitleUnit?: string;
    defaultPosition?: any;
    popupCustomStyle?: string;
  } | null>(null);
  // US-102820 / US-102821 Measure Tool: live geodesic readouts + active tab.
  const [measureMode, setMeasureMode] = useState<MeasureMode>('distance');
  const [distanceMeters, setDistanceMeters] = useState(0);
  const [areaSqMeters, setAreaSqMeters] = useState(0);
  const [searchableFeatures, setSearchableFeatures] = useState<any[]>([]);
  // Editor context is only a fallback while a layer is being configured in the panel.
  const {
    popupTitle: editorPopupTitle,
    defaultPosition: editorDefaultPosition,
    popupTitleUnit: editorPopupTitleUnit,
    popupCustomStyle: editorPopupCustomStyle,
  } = useLayerProperitesContext();
  const [clickScreenPos, setClickScreenPos] = useState({ x: 0, y: 0 });
  const { drawFeatureModalVisible, queryToolModalVisible, proximityToolModalVisible } = useLegendUI();

  const handlePopoverClose = (): void => {
    setPopoverVisible(false);
    setActivePopupConfig(null);
  };

  const popupTitle = activePopupConfig?.popupTitle ?? editorPopupTitle;
  const popupTitleUnit = activePopupConfig?.popupTitleUnit ?? editorPopupTitleUnit;
  const defaultPosition = activePopupConfig?.defaultPosition ?? editorDefaultPosition;
  const popupCustomStyle = activePopupConfig?.popupCustomStyle ?? editorPopupCustomStyle;

  const { isViewerReady } = useCesiumViewer({
    container: containerId,
    viewerRef,
    ionAccessToken: import.meta.env.VITE_APP_CESIUM_ACCESS_TOKEN as string,
    viewerOptions: {
      animation: false,
      timeline: false,
      shouldAnimate: true,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      homeButton: false,
      geocoder: false,
      baseLayerPicker: true,
      sceneMode: Cesium.SceneMode.SCENE2D,
    },
    hideBaseLayerPickerButton: true,
  });

  useCesiumCamera({
    isViewerReady,
    viewerRef,
    defaultMapPositions,
    boundingLayers,
    mapId,
  });

  const { highlightFeature } = useCesiumEffects({
    isViewerReady,
    viewerRef,
    searchableFeatures,
  });

  useCesiumLayers({
    isViewerReady,
    viewerRef,
    layers,
    setSearchableFeatures,
  });

  useCesiumPopup({
    isViewerReady,
    viewerRef,
    layers,
    setPopoverContent,
    setPopoverVisible,
    setClickScreenPos,
    setActivePopupConfig,
    highlightFeature,
    disabled: drawFeatureModalVisible || queryToolModalVisible || proximityToolModalVisible || currentTool === 'measure',
  });

  // useCesiumTools({
  //   isViewerReady,
  //   viewerRef,
  //   currentTool,
  //   drawingMode,
  //   isDrawingPolygon,
  //   setIsDrawingPolygon,
  //   pointsRef,
  //   startPointRef,
  //   drawPolygon,
  //   addPointEntity,
  // });

  const [measureError, setMeasureError] = useState<string | null>(null);

  const { reset: resetMeasure } = useMeasureTool({
    isViewerReady,
    viewerRef,
    active: currentTool === 'measure',
    mode: measureMode,
    onDistanceChange: setDistanceMeters,
    onAreaChange: setAreaSqMeters,
    onError: setMeasureError,
  });

  const closeMeasure = () => {
    resetMeasure();
    setMeasureMode('distance');
    setCurrentTool('');
  };

  return (
    <Fragment>
      <div id={containerId} className="cesium-viewer mapportal-map-container">
        {isLayerLoading && (
          <div className="mapportal-map__loading">
            <Spin />
          </div>
        )}
        {currentTool === 'measure' && (
          <div className="query-tool__draw-banner">
            {measureMode === 'area' ? (
              <>
                Click the starting point to finish drawing.
                <span className="query-tool__draw-banner-hint">Right-click to cancel the drawing.</span>
              </>
            ) : (
              <>
                Double-click to finish drawing.
                <span className="query-tool__draw-banner-hint">Right-click to cancel the drawing.</span>
              </>
            )}
          </div>
        )}
        <MeasurePanel
          show={currentTool === 'measure'}
          mode={measureMode}
          setMode={setMeasureMode}
          distanceMeters={distanceMeters}
          areaSqMeters={areaSqMeters}
          onReset={resetMeasure}
          onClose={closeMeasure}
          error={measureError}
        />
        <FeaturePopover
          visible={popoverVisible}
          content={popoverContent}
          onClose={handlePopoverClose}
          containerId={containerId}
          popupTitle={popupTitle}
          popupTitleUnit={popupTitleUnit}
          defaultPosition={defaultPosition}
          popupCustomStyle={popupCustomStyle}
          pixelPos={clickScreenPos}
        />
        <MapSearch searchableFeatures={searchableFeatures} viewerRef={viewerRef} />
      </div>
    </Fragment>
  );
};

export default MapPortalMap;
